import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { listTemplates, fetchTemplate, fetchCampaign, createCampaignApi, updateCampaignTemplatesApi, createTemplateApi, listCampaignsApi, listKbForCampaign, attachKbToCampaign, detachKbFromCampaign } from '../api/api';
import ConfirmationModal from '../components/ConfirmationModal';
import { FaTimes } from 'react-icons/fa';
import {FileUpload} from "@cloudscape-design/components"
import { presign } from '../api/api';
import axios from 'axios';

const CampaignEditor = ({ setAlert }) => {
  const history = useHistory();
  const { name } = useParams();
  const [campaignName, setCampaignName] = useState('');
  const [templates, setTemplates] = useState([]); // all templates
  const [selected, setSelected] = useState([]); // selected template names (unowned or own-queue)
  const [templatesToVersion, setTemplatesToVersion] = useState([]); // templates owned by other queues — will be cloned
  const [initial, setInitial] = useState([]);
  const [newTemplates, setNewTemplates] = useState([]); // templates created inline
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveConfirm, setSaveConfirm] = useState(false);
  const [allCamps, setAllCamps] = useState('')
  const [inputError, setInputError] = useState([false]);
  const [upload, setUpload] = useState([])
  const [campaignKbFiles, setCampaignKbFiles] = useState([])

    const handleFileAdd = ({ detail }) => {
    const newFiles = detail.value;
    setUpload(newFiles);
    // console.log("UPLOAD: ", upload)
  };

  useEffect(() => {
    const load = async () => {
      try {
        const tmplRes = await listTemplates();
        setTemplates(tmplRes.templates || []);
        const res = await listCampaignsApi();
        setAllCamps(res.campaigns)
        if (name) {
          const campRes = await fetchCampaign(name);
          setCampaignName(campRes.campaignName);
          setSelected(campRes.templates || []);
          setInitial(campRes.templates || []);
          try {
            const kbRes = await listKbForCampaign(name);
            setCampaignKbFiles(kbRes.files || []);
          } catch (_) {}
        }
      } catch (err) {
        console.error('Failed to load data', err);
        setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to load queue.' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [name]);
  

  const toggle = (tpl) => {
    const tplName = tpl.templateName;
    const owner = tpl.campaignName;
    // Template owned by a different queue → goes into the "version" bucket
    if (owner && owner !== campaignName) {
      setTemplatesToVersion(prev =>
        prev.includes(tplName) ? prev.filter(x => x !== tplName) : [...prev, tplName]
      );
    } else {
      setSelected(s => s.includes(tplName) ? s.filter(x => x !== tplName) : [...s, tplName]);
    }
  };

  const versionName = (tplName) =>
    `${tplName}-${campaignName}`.replaceAll(' ', '-').replaceAll('_', '-');

  const addTemplate = () => {
    setNewTemplates(t => [
      ...t,
      {
        templateName: '',
        questions: [{ questionName: '', description: '',expected: '', type: 'Compliance', weight: ''  }]
      }
    ]);
  };

  const updateTemplate = (index, key, value) => {
    setNewTemplates(t => t.map((tpl, i) => i === index ? { ...tpl, [key]: value } : tpl));
  };

  const addQuestion = (tplIndex) => {
    setNewTemplates(t => t.map((tpl, i) =>
      i === tplIndex ? { ...tpl, questions: [...tpl.questions, { questionName: '', description: '',expected: "", type: 'Compliance', weight: '' }] } : tpl
    ));
  };

  const updateQuestion = (tplIndex, qIndex, key, value) => {
    setNewTemplates(t => t.map((tpl, i) =>
      i === tplIndex ? { ...tpl, questions: tpl.questions.map((q, qi) => qi === qIndex ? { ...q, [key]: value } : q) } : tpl
    ));
  };

  const removeQuestion = (tplIndex, qIndex) => {
    setNewTemplates(t => t.map((tpl, i) =>
      i === tplIndex ? { ...tpl, questions: tpl.questions.filter((_, qi) => qi !== qIndex) } : tpl
    ));
  };

  const removeTemplate = (index) => {
    setNewTemplates(t => t.filter((_, i) => i !== index));
  };

  const handleRemoveKbFile = async (file) => {
    try {
      await detachKbFromCampaign(campaignName, file.s3Key);
      setCampaignKbFiles(prev => prev.filter(f => f.s3Key !== file.s3Key));
    } catch (err) {
      console.error('Failed to remove KB file', err);
      setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to remove KB file.' });
    }
  };

  const uploadKbFiles = async (targetCampaign) => {
    for (const file of upload) {
      const s3Up = await presign(file.name, 'kb', targetCampaign);
      await axios.put(s3Up.url, file);
      await attachKbToCampaign(targetCampaign, { fileName: file.name, s3Key: s3Up.key });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
        for (let i =0; i<= allCamps.length -1 ; i++) {
          if (allCamps[i].campaignName === campaignName && name != allCamps[i].campaignName) {
            setInputError([true, `Queue name '${allCamps[i].campaignName}' already exists.`])
            setSaveConfirm(false);
            return;
          }
        }
      // Create versioned copies of scorecards owned by other queues
      const versionedNames = [];
      for (const tplName of templatesToVersion) {
        const original = await fetchTemplate(tplName);
        const newName = versionName(tplName);
        await createTemplateApi({
          isVersionCreation: true,
          newTemplateName: newName,
          originalTemplateName: tplName,
          campaign: campaignName,
          questions: original.questions,
          sections: original.sections,
          scoringMode: original.scoringMode,
          complianceThreshold: original.complianceThreshold
        });
        versionedNames.push(newName);
      }

      if (!name) {
        const createdNames = [];
        for (const tpl of newTemplates) {
          if (!tpl.templateName.trim()) throw new Error('Scorecard name required');
          await createTemplateApi({ templateName: tpl.templateName, campaign: campaignName, questions: tpl.questions });
          createdNames.push(tpl.templateName);
        }
        // Create campaign first so KB attach has a valid campaign to reference
        await createCampaignApi({ campaignName, templates: [...selected, ...createdNames, ...versionedNames] });
        if (upload.length !== 0) {
          await uploadKbFiles(campaignName);
        }
      } else {
        const createdNames = [];
        for (const tpl of newTemplates) {
          if (!tpl.templateName.trim()) throw new Error('Scorecard name required');
          await createTemplateApi({ templateName: tpl.templateName, campaign: campaignName, questions: tpl.questions });
          createdNames.push(tpl.templateName);
        }
        if (upload.length !== 0) {
          await uploadKbFiles(campaignName);
        }
        const allNew = [...selected, ...createdNames, ...versionedNames];
        const toAdd = allNew.filter(t => !initial.includes(t));
        const toRemove = initial.filter(t => !allNew.includes(t));
        for (const t of toAdd) {
          await updateCampaignTemplatesApi(campaignName, { templateName: t, action: 'add' });
        }
        for (const t of toRemove) {
          await updateCampaignTemplatesApi(campaignName, { templateName: t, action: 'remove' });
        }
      }
      setUpload([])
      setAlert?.({ variant: 'success', heading: 'Queue Saved', text: 'Queue saved successfully.' });
      history.push('/campaigns');
    } catch (err) {
      console.error('Save failed', err);
      setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to save queue.' });
    } finally {
      setSaving(false);
    }
  };


  if (loading) return <div className="text-center mt-20 text-gray-500">Loading...</div>;

  return (
    <>
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{name ? 'Edit Queue' : 'New Queue'}</h1>
      </div>
      <div className="card space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Queue Name</label>
          <input value={campaignName} onChange={e=>setCampaignName(e.target.value)} className="input-field" disabled={!!name} />
        </div>
        {campaignKbFiles.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Knowledge Base Files</label>
            <div className="space-y-1">
              {campaignKbFiles.map(file => (
                <div key={file.s3Key} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200">
                  <span className="text-sm text-gray-700">{file.fileName}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveKbFile(file)}
                    className="text-red-500 hover:text-red-700 ml-2"
                    title="Remove"
                  >
                    <FaTimes />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Upload Knowledge Base Files</label>
          <FileUpload
              onChange={handleFileAdd}
              value={upload}
              i18nStrings={{
              uploadButtonText: e =>
                  e ? "Upload files" : "Upload file",
              dropzoneText: e =>
                  e
                  ? "Drop files to upload"
                  : "Drop file to upload",
              removeFileAriaLabel: e =>
                  `Remove file ${e + 1}`,
              limitShowFewer: "Show fewer files",
              limitShowMore: "Show more files",
              errorIconAriaLabel: "Error",
              warningIconAriaLabel: "Warning"
              }}
              multiple
              showFileLastModified
              showFileSize
              showFileThumbnail
              tokenLimit={3}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Scorecards</label>
          <div className="space-y-2">
            {templates.map(t => {
              const isOtherQueue = t.campaignName && t.campaignName !== campaignName;
              const isChecked = isOtherQueue
                ? templatesToVersion.includes(t.templateName)
                : selected.includes(t.templateName);
              return (
                <div key={t.templateName}>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggle(t)}
                    />
                    <span>{t.templateName}</span>
                    {isOtherQueue && (
                      <span className="text-xs text-gray-400 ml-1">— owned by <em>{t.campaignName}</em></span>
                    )}
                  </label>
                  {isOtherQueue && isChecked && (
                    <p className="text-xs text-amber-600 ml-6 mt-0.5">
                      Already attached to queue &quot;{t.campaignName}&quot;. A new version named &quot;{versionName(t.templateName)}&quot; will be created for this queue.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {inputError[0] && (
          <div className="custom-alert">
            <span>{inputError[1]}</span>
            <button className="dismiss-btn" onClick={() => setInputError([false])}>
              <FaTimes />
            </button>
          </div>
        )}  
        <div className="flex justify-end">
          <button onClick={() => setSaveConfirm(true)} disabled={saving} className="btn-primary">
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
    <ConfirmationModal
      open={saveConfirm}
      title="Save Confirmation"
      message={`Are you sure you want to save the changes to ${campaignName || 'this Queue'}?`}
      confirmText="Confirm Save"
      onClose={() => setSaveConfirm(false)}
      onConfirm={handleSave}
    />
    </>
  );
};

export default CampaignEditor;